/**
 * This period against the one before it.
 *
 * The question anybody asks of any tracker, and until now the answer was to
 * print two reports and hold them up next to each other.
 *
 * **The comparison period is the immediately preceding window of the same
 * length, and that is not a shortcut.** Two freely chosen ranges look more
 * capable and produce a worse answer: a count of 30 against a count of 14 is
 * meaningless, a mean over four days is not comparable with a mean over forty,
 * and nothing in the arithmetic can warn the reader. Equal length by
 * construction removes the whole class of mistake, and "the fortnight before
 * this fortnight" is what people mean anyway.
 *
 * Every number comes from `summariseTrack`, so a comparison and the printed
 * report cannot disagree about either side of it.
 */

import type { Entry } from '../model/entry.js'
import { shiftDayKey } from '../model/timestamp.js'
import type { Track } from '../model/track.js'
import { isQuantitative } from '../model/track.js'
import { rangeDays, type ResolvedRange } from './range.js'
import { buildSeries } from './series.js'
import { aggregateDecimals, describeQuantity } from './statistics.js'
import { summariseTrack, type ReportRow } from './report.js'

export type ChangeDirection = 'up' | 'down' | 'level'

export interface TrackComparison {
  readonly track: Track
  /** This period. */
  readonly current: ReportRow
  /** The equally long period immediately before it. */
  readonly previous: ReportRow
  /**
   * The change in the headline number, and what it is a change *in*.
   *
   * `null` when either side has nothing recorded, or when the track has no
   * magnitude — a marker's count can be compared but its "average" cannot,
   * because it does not have one.
   */
  readonly change: {
    readonly kind: 'mean' | 'count'
    readonly from: number
    readonly to: number
    readonly delta: number
    readonly direction: ChangeDirection
  } | null
}

export interface Comparison {
  readonly current: ResolvedRange
  readonly previous: ResolvedRange
  readonly days: number
  readonly tracks: readonly TrackComparison[]
  /** Entries in each period, for the headline. */
  readonly currentEntries: number
  readonly previousEntries: number
}

/**
 * The equally long window ending the day before `range` starts.
 *
 * Derived rather than asked for, so the two sides cannot be different lengths.
 * `label` is honest about what it is: a reader six weeks later has no way to
 * know what "the previous period" meant, and the absolute days do.
 */
export function previousPeriod(range: ResolvedRange): ResolvedRange {
  const days = rangeDays(range)
  const toDay = shiftDayKey(range.fromDay, -1)
  const fromDay = shiftDayKey(toDay, -(days - 1))
  const length = range.toEpochMs - range.fromEpochMs
  return {
    fromDay,
    toDay,
    // Shifted by the same span, so a comparison across a clock change keeps the
    // two windows the same number of *hours* as well as the same number of days.
    fromEpochMs: range.fromEpochMs - length - 1,
    toEpochMs: range.fromEpochMs - 1,
    label: `${fromDay} to ${toDay}`,
    // A custom selection, because that is what it is: derived from the current
    // range rather than chosen from a preset, and a preset label here would go
    // stale the moment the range it was derived from moved.
    selection: { kind: 'custom', fromDay, toDay },
  }
}

function directionOf(delta: number, scale: number): ChangeDirection {
  /*
   * A band, not `!== 0`. Two means computed from self-reported readings differ
   * in the third decimal place almost every time, and reporting that as "up"
   * would make the word meaningless — the same reasoning as `trendOf`'s steady
   * band, and for the same reason it is derived from the data rather than from
   * anything the user typed at setup.
   */
  const threshold = scale * 0.02
  if (Math.abs(delta) <= threshold) {
    return 'level'
  }
  return delta > 0 ? 'up' : 'down'
}

function changeBetween(
  current: ReportRow,
  previous: ReportRow,
): TrackComparison['change'] {
  if (current.kind === 'noData' || previous.kind === 'noData') {
    return null
  }

  if (current.kind === 'quantitative' && previous.kind === 'quantitative') {
    const from = previous.statistics.mean
    const to = current.statistics.mean
    const spread = Math.max(
      current.statistics.max - current.statistics.min,
      previous.statistics.max - previous.statistics.min,
      Math.abs(to),
      1,
    )
    return {
      kind: 'mean',
      from,
      to,
      delta: to - from,
      direction: directionOf(to - from, spread),
    }
  }

  /*
   * Both categorical, so the comparable number is how often it was recorded.
   * Not a mean of anything: a marker has no magnitude and an unordered enum has
   * no order, and inventing one for a side-by-side would be the exact failure
   * `quantify → null` exists to prevent.
   */
  if (current.kind === 'categorical' && previous.kind === 'categorical') {
    const from = previous.count
    const to = current.count
    return {
      kind: 'count',
      from,
      to,
      delta: to - from,
      direction: directionOf(to - from, Math.max(from, to, 1)),
    }
  }

  // One side quantitative and the other categorical means the track's value
  // type was changed mid-history, which the repositories refuse — so this is
  // unreachable rather than merely unlikely. No number is the honest answer.
  return null
}

export function comparePeriods(
  tracks: readonly Track[],
  entries: readonly Entry[],
  range: ResolvedRange,
): Comparison {
  const previous = previousPeriod(range)

  const inCurrent = entries.filter(
    (entry) =>
      entry.at.epochMs >= range.fromEpochMs && entry.at.epochMs <= range.toEpochMs,
  )
  const inPrevious = entries.filter(
    (entry) =>
      entry.at.epochMs >= previous.fromEpochMs && entry.at.epochMs <= previous.toEpochMs,
  )

  return {
    current: range,
    previous,
    days: rangeDays(range),
    currentEntries: inCurrent.length,
    previousEntries: inPrevious.length,
    tracks: tracks.map((track) => {
      const currentRow = summariseTrack(track, buildSeries(track, inCurrent, range))
      const previousRow = summariseTrack(track, buildSeries(track, inPrevious, previous))
      return {
        track,
        current: currentRow,
        previous: previousRow,
        change: changeBetween(currentRow, previousRow),
      }
    }),
  }
}

/**
 * One track's change as a sentence, worded here rather than in a component.
 *
 * Never a judgement. "Up" and "down" are directions; whether either is good is
 * a question about the subject matter, which this app does not know and has no
 * business guessing. A track called Stress and a track called Sleep both go up.
 */
export function describeComparison(comparison: TrackComparison): string {
  const { track, change } = comparison
  if (change === null) {
    if (comparison.current.kind === 'noData' && comparison.previous.kind === 'noData') {
      return 'Not recorded in either period.'
    }
    return comparison.current.kind === 'noData'
      ? 'Recorded before, but not this time.'
      : 'New this period — nothing before it to compare with.'
  }

  if (change.kind === 'count') {
    const times = (n: number) => `${String(n)} ${n === 1 ? 'time' : 'times'}`
    if (change.direction === 'level') {
      return `About as often — ${times(change.to)}, against ${times(change.from)}.`
    }
    return `${times(change.to)}, against ${times(change.from)}.`
  }

  const decimals = aggregateDecimals(track)
  const from = describeQuantity(track, Number(change.from.toFixed(decimals)))
  const to = describeQuantity(track, Number(change.to.toFixed(decimals)))
  if (change.direction === 'level') {
    return `About the same — ${to}, against ${from}.`
  }
  const size = Math.abs(change.delta).toFixed(decimals)
  return `${change.direction === 'up' ? 'Up' : 'Down'} ${size} — ${to}, against ${from}.`
}

/** Whether anything in the comparison is worth drawing. */
export function hasComparableData(comparison: Comparison): boolean {
  return comparison.tracks.some((track) => track.change !== null)
}

/** The tracks worth showing first: those with a magnitude and a real change. */
export function rankComparisons(comparison: Comparison): readonly TrackComparison[] {
  return [...comparison.tracks].sort((one, other) => {
    const rank = (item: TrackComparison) => {
      if (item.change === null) {
        return -1
      }
      // Relative, so a 1-point move on a 1–10 scale outranks a 1-unit move on a
      // track that runs to hundreds.
      const base = Math.max(Math.abs(item.change.from), 1)
      const magnitude = Math.abs(item.change.delta) / base
      return isQuantitative(item.track.kind) ? magnitude : magnitude * 0.99
    }
    return rank(other) - rank(one)
  })
}
